import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Grid, Box, Typography, Paper, 
  Card, CardContent, Stack, Alert, LinearProgress, Chip, Divider,
  Tabs, Tab, Button, 
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import { getCurrentAcademicYearId } from '../../../utils/academicYearContext';
import { usePhase } from '../../../contexts/PhaseContext';
import AppLoader from '../../../components/AppLoader';
import { 
  People as PeopleIcon,
  School as SchoolIcon,
  Groups as GroupsIcon,
  Subject as SubjectIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';



import useCurrentUser from '../../../utils/useCurrentUser';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';

// Import real chart/function components
import StudentsPerClassChart from '../../../components/School_Admin/Dashboard/StudentsPerClassChart';
import TeachersPerSubjectChart from '../../../components/School_Admin/Dashboard/TeachersPerSubjectChart';
import TeachersPerClassChart from '../../../components/School_Admin/Dashboard/TeachersPerClassChart';
import StaffDistributionChart from '../../../components/School_Admin/Dashboard/StaffDistributionChart';
import QuickActions from '../../../components/School_Admin/Dashboard/QuickActions';
import UpcomingEvents from '../../../components/School_Admin/Dashboard/UpcomingEvents';
import { subjectChartLabel } from '../../../utils/subjectLabel.generated';

const SchoolOverviewPage = () => {
  const { t } = useTranslation();
  const { activePhase, getPhaseLabel } = usePhase();
  // Nursery/Primary schools have no departments — hide that card for them.
  const isNurPriPhase = String(activePhase || '').toLowerCase() === 'nur_pri';
  const { can } = useCurrentUser();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [performanceAlerts, setPerformanceAlerts] = useState([]);

  const getStudentLabel = (row) => {
    const studentName = (row?.studentName || '').toString().trim();
    return studentName || (t('common.unknown', { defaultValue: 'Unknown' }));
  };

  // Currency formatter for CFA (XAF) - display as amount + CFA (e.g. 123 456 CFA)
  const formatCFA = (amount) => {
    const n = Number(amount || 0);
    return `${n.toLocaleString()} CFA`;
  };

  // Loaders defined early and memoized to avoid re-creation each render
  const loadAdvancedAnalytics = useCallback(async () => {
    try {
      const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
      const headers = {
        ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
        ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
        ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
        ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
      };
      const analytics = await fetchJson('/api/school-admin/advanced-analytics', { headers });
      setAdvancedAnalytics(analytics);
    } catch (error) {
      console.error('Failed to load advanced analytics:', error);
    }
  }, [activePhase]);

  // Predictive insights removed per requirements

  const loadPerformanceAlerts = useCallback(async () => {
    try {
      const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
      const headers = {
        ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
        ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
        ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
        ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
      };
      const alerts = await fetchJson('/api/school-admin/performance-alerts', { headers });
      setPerformanceAlerts(alerts);
    } catch (error) {
      console.error('Failed to load performance alerts:', error);
    }
  }, []);

  // Enhanced analytics data (populated from API)
  const [advancedAnalytics, setAdvancedAnalytics] = useState({
    studentPerformance: { trends: [], trendsSeq: [], bySubject: [], riskStudents: [] },
    financialMetrics: { revenue: 0, expenses: 0, profit: 0, monthlyTrend: [] },
    riskManagement: { lowPerformanceStudents: [], chronicAbsentees: [], overdueFeesTopDebtors: [] },
    operationalMetrics: null,
    predictiveInsights: []
  });

  // Dashboard data loader
  const fetchDashboardData = useCallback(async () => {
    const ctrl = new AbortController();
    let mounted = true;
    let maxGuard;
    try {
      setLoading(true);
      // Instant cache prefill to avoid flash (scoped by school|phase|ay)
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
        const cacheKey = `schoolOverview:data:${ctxKey}`;
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached && typeof cached === 'object') setDashboardData(cached);
        }
      } catch (_) {}
      // 2s guard to avoid feeling stuck
      try { maxGuard = setTimeout(() => { if (mounted) setLoading(false); }, 2000); } catch(_) {}
      let data;
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
        const headers = {
          ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
          ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
          ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
          ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
        };
        // Primary route (API-prefixed)
        data = await fetchJson('/api/school-admin/school-overview', { signal: ctrl.signal, headers });
      } catch (primaryErr) {
        if (primaryErr?.name === 'AbortError') return;
        console.error('[SchoolOverview] Primary fetch failed:', primaryErr);

        const primaryMsg = String(primaryErr?.message || '');
        const is403Expired = primaryMsg.includes('HTTP 403') && (
          primaryMsg.toLowerCase().includes('free trial expired') ||
          primaryMsg.toLowerCase().includes('subscription expired')
        );
        if (is403Expired) {
          // Keep rendering the page with whatever we already have (cached/previous) and avoid hard error UI.
          if (mounted) setError('');
          return;
        }

        // Fallback to non-API alias exposed by server
        try {
          const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
          const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
          const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
          const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
          const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
          const headers = {
            ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
            ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
            ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
            ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
          };
          data = await fetchJson('/school-admin/school-overview', { signal: ctrl.signal, headers });
        } catch (fallbackErr) {
          if (fallbackErr?.name === 'AbortError') return;
          console.error('[SchoolOverview] Fallback fetch failed:', fallbackErr);

          const fallbackMsg = String(fallbackErr?.message || '');
          const is403ExpiredFallback = fallbackMsg.includes('HTTP 403') && (
            fallbackMsg.toLowerCase().includes('free trial expired') ||
            fallbackMsg.toLowerCase().includes('subscription expired')
          );
          if (is403ExpiredFallback) {
            if (mounted) setError('');
            return;
          }

          // Surface meaningful error
          const msg = fallbackErr?.message || primaryErr?.message || (t('schoolAdmin.dashboard.error') || 'Failed to load dashboard data');
          if (mounted) setError(msg);
          return;
        }
      }
      if (mounted) setDashboardData(data);
      // Persist cache for next paint
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
        const cacheKey = `schoolOverview:data:${ctxKey}`;
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch(_) {}

      // Load additional analytics in parallel for speed
      await Promise.all([
        loadAdvancedAnalytics(),
        loadPerformanceAlerts()
      ]);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[SchoolOverview] Unexpected error:', err);
      if (mounted) setError(err?.message || (t('schoolAdmin.dashboard.error') || 'Failed to load dashboard data'));
    } finally {
      if (mounted) setLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    }
    return () => {
      mounted = false;
      try { ctrl.abort(); } catch (_) {}
    };
  }, [t, loadAdvancedAnalytics, loadPerformanceAlerts]);

  // Initial load regardless of capability flags (avoid getting stuck when flags are undefined)
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh on phase/academic year change events (no full reload)
  useEffect(() => {
    const lastAyRef = { current: getCurrentAcademicYearId() };
    const lastPhaseRef = { current: activePhase };

    const onYear = (e) => {
      const newId = e?.detail?.id ?? getCurrentAcademicYearId();
      if (newId && newId === lastAyRef.current) return;
      lastAyRef.current = newId;
      fetchDashboardData();
    };
    const onPhase = (e) => {
      const newCode = e?.detail?.code ?? activePhase;
      if (newCode && newCode === lastPhaseRef.current) return;
      lastPhaseRef.current = newCode;
      fetchDashboardData();
    };
    window.addEventListener('app:ay:changed', onYear);
    window.addEventListener('app:phase:changed', onPhase);
    return () => {
      window.removeEventListener('app:ay:changed', onYear);
      window.removeEventListener('app:phase:changed', onPhase);
    };
  }, [fetchDashboardData, activePhase]);

  // While loading data, show a centered loader (do not block on capability flags readiness)
  if (loading && !dashboardData) {
    return (
      <AppLoader
        variant="circular3"
        size={60}
        centered
        minHeight="60vh"
        messages={[]}
      />
    );
  }

  // Removed Access Denied gate: always render overview content


  const handleExportReport = async (type) => {
    try {
      const response = await fetchJson('/api/school-admin/export-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, dateRange: 'current' })
      });
      
      // Handle file download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `school-analytics-${type}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // loading handled above with permsReady



  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header with Actions */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          {t('schoolAdmin.dashboard.title') || 'Advanced School Analytics Hub'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* The phase chip is gone: the scope bar above every page already
              names the phase and the year, and one fact shown twice is one
              more place for it to disagree with itself. */}
          {dashboardData?.effectiveAcademicYear?.name && (
            <Chip
              label={`${t('schoolAdmin.dashboard.yearLabel') || 'Year'}: ${dashboardData.effectiveAcademicYear.name}`}
              color="default"
              variant="outlined"
            />
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchDashboardData();
              // Also kick ancillary loaders (non-blocking)
              loadAdvancedAnalytics();
              loadPerformanceAlerts();
            }}
          >
            {t('schoolAdmin.dashboard.refreshData') || 'Refresh Data'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleExportReport('comprehensive')}
          >
            {t('schoolAdmin.dashboard.exportReport') || 'Export Report'}
          </Button>
          {/* Custom Analytics removed */}
        </Box>
      </Box>

      {/* Performance Alerts */}
      {performanceAlerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('schoolAdmin.dashboard.performanceAlertsTitle') || 'Performance Alerts'}
          </Typography>
          <Grid container spacing={2}>
            {performanceAlerts.map((alert, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Alert 
                  severity={alert.severity} 
                  icon={alert.severity === 'error' ? <ErrorIcon /> : alert.severity === 'warning' ? <WarningIcon /> : <CheckCircleIcon />}
                >
                  <Typography variant="subtitle2">{alert.title}</Typography>
                  <Typography variant="body2">{alert.message}</Typography>
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Enhanced Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab label={t('schoolAdmin.dashboard.tabs.overview') || 'Overview'} />
        <Tab label={t('schoolAdmin.dashboard.tabs.studentPerformance') || 'Student Performance'} />
        <Tab label={t('schoolAdmin.dashboard.tabs.financialAnalytics') || 'Financial Analytics'} />
        <Tab label={t('schoolAdmin.dashboard.tabs.riskManagement') || 'Risk Management'} />
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          {/* Top Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <SummaryCard 
                title={t('schoolAdmin.dashboard.totalStudents') || 'Total Students'}
                value={dashboardData?.totalStudents}
                icon={<PeopleIcon fontSize="large" />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SummaryCard 
                title={t('schoolAdmin.dashboard.totalTeachers') || 'Total Teachers'}
                value={dashboardData?.totalTeachers}
                icon={<SchoolIcon fontSize="large" />}
                color="secondary"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SummaryCard 
                title={t('schoolAdmin.dashboard.totalStaff') || 'Total Staff'}
                value={dashboardData?.totalStaff}
                icon={<GroupsIcon fontSize="large" />}
                color="success"
              />
            </Grid>
            {!isNurPriPhase && (
              <Grid item xs={12} md={3}>
                <SummaryCard
                  title={t('schoolAdmin.dashboard.departments') || 'Departments'}
                  value={dashboardData?.departmentCount}
                  icon={<SubjectIcon fontSize="large" />}
                  color="warning"
                />
              </Grid>
            )}
          </Grid>
          
          {/* Main Charts Area */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                      {t('schoolAdmin.dashboard.studentsPerClass') || 'Students per Class'}
                    </Typography>
                    <StudentsPerClassChart data={dashboardData?.studentsPerClass} />
                  </Paper>
                </Grid>
                {/* NO DEPARTMENTS IN NURSERY/PRIMARY, so the card asks the
                    question that phase can answer: how many teachers reach each
                    CLASS — its class teacher, plus any specialist who teaches a
                    subject into it.
                    Secondary shows teachers per SUBJECT, which is what that
                    series has always contained — it groups by subject.name and
                    never touches the departments table. */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                      {isNurPriPhase
                        ? (t('schoolAdmin.dashboard.teachersPerClass') || 'Teachers per Class')
                        : (t('schoolAdmin.dashboard.teachersPerSubject') || 'Teachers per Subject')}
                    </Typography>
                    {isNurPriPhase
                      ? <TeachersPerClassChart data={dashboardData?.teachersPerClass} />
                      : <TeachersPerSubjectChart data={dashboardData?.teachersPerSubject || dashboardData?.teachersPerDepartment} />}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.staffDistribution') || 'Staff Distribution'}
                </Typography>
                <StaffDistributionChart data={dashboardData?.staffDistribution} />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Student Performance Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.performanceBySequence') || 'Performance by Sequence (1st - 6th)'}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={advancedAnalytics?.studentPerformance?.trendsSeq || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sequence" />
                    <YAxis domain={[0, 20]} />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="average" stroke="#8884d8" name="Average Score" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('schoolAdmin.dashboard.performanceBySubject') || 'Performance by Subject'}
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Subject</TableCell>
                          <TableCell>Performance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* THE CLIPPED FORM, as on the phone's chart. This panel
                            sits in a third of the row, so "Further Mathematics"
                            wraps to two lines and the column stops lining up.
                            The school's own short form is what it writes on a
                            timetable, and the full name is on the title so
                            nothing is lost to anyone who wants it. */}
                        {(advancedAnalytics?.studentPerformance?.bySubject || []).map((s) => (
                          <TableRow key={s.subject}>
                            <TableCell title={s.subject}>
                              {subjectChartLabel(s.subject, s.subjectCode)}
                            </TableCell>
                            <TableCell>{Number(s.performance).toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                        {(!advancedAnalytics?.studentPerformance?.bySubject || advancedAnalytics.studentPerformance.bySubject.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              {t('schoolAdmin.dashboard.noSubjectPerformanceData') || 'No subject performance data'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('schoolAdmin.dashboard.performanceByClass') || 'Performance by Class'}
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {/* NO STREAM COLUMN. The class name already carries the
                              stream wherever a school uses one — "Form 5 Science"
                              is the class — so the column repeated half of it or
                              sat empty, and on a phone it took a third of the
                              width to do so. */}
                          <TableCell>{t('schoolAdmin.dashboard.class') || 'Class'}</TableCell>
                          <TableCell>Performance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(advancedAnalytics?.studentPerformance?.byClass || []).map((c, index) => (
                          <TableRow key={`${c.class}-${c.stream || ''}-${index}`}>
                            <TableCell>{c.class}</TableCell>
                            <TableCell>{Number(c.performance).toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                        {(!advancedAnalytics?.studentPerformance?.byClass || advancedAnalytics.studentPerformance.byClass.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              {t('schoolAdmin.dashboard.noSubjectPerformanceData') || 'No class performance data'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

        {/* Financial Analytics Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.financialOverview') || 'Financial Overview'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {formatCFA(advancedAnalytics?.financialMetrics?.revenue || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schoolAdmin.dashboard.revenue') || 'Revenue'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {formatCFA(advancedAnalytics?.financialMetrics?.expenses || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schoolAdmin.dashboard.expenses') || 'Expenses'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Typography variant="h4" color="primary.main">
                        {formatCFA(advancedAnalytics?.financialMetrics?.profit || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schoolAdmin.dashboard.profit') || 'Profit'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.monthlyTrends') || 'Monthly Financial Trends'}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={advancedAnalytics?.financialMetrics?.monthlyTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stackId="1" stroke="#8884d8" fill="#8884d8" name={t('schoolAdmin.dashboard.revenue') || 'Revenue'} />
                    <Area type="monotone" dataKey="expenses" stackId="1" stroke="#ff7300" fill="#ff7300" name={t('schoolAdmin.dashboard.expenses') || 'Expenses'} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Risk Management Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.studentsAtRiskTitle') || 'Students at Risk'}
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('schoolAdmin.dashboard.studentName') || 'Student'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.class') || 'Class'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.avgScore90d') || 'Avg Score (90d)'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.lastSequenceAvg') || 'Last Sequence Avg'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.weakestCoreSubject') || 'Weakest Core Subject'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.actions') || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((advancedAnalytics?.riskManagement?.lowPerformanceStudents || advancedAnalytics?.studentPerformance?.riskStudents || [])).map((s, index) => (
                      <TableRow key={index}>
                        <TableCell>{getStudentLabel(s)}</TableCell>
                        <TableCell>{s.className || '—'}</TableCell>
                        <TableCell>{Number(s.avg90d ?? s.avg ?? 0).toFixed(2)}</TableCell>
                        <TableCell>
                          {s.lastSequenceAvg != null ? Number(s.lastSequenceAvg).toFixed(2) : '—'}
                        </TableCell>
                        <TableCell>{s.weakestCoreSubject || '—'}</TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined">
                            {t('schoolAdmin.dashboard.viewDetails') || 'View Details'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!(advancedAnalytics?.riskManagement?.lowPerformanceStudents || advancedAnalytics?.studentPerformance?.riskStudents || []).length) && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          {t('schoolAdmin.dashboard.noRiskStudents') || 'No risk students identified in the last 90 days'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Chronic Absentees */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.riskChronicAbsenteesTitle') || 'Chronic Absentees (last 30 days)'}
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('schoolAdmin.dashboard.studentName') || 'Student'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.class') || 'Class'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.attendanceRate') || 'Attendance Rate'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(advancedAnalytics?.riskManagement?.chronicAbsentees || []).map((s, index) => (
                      <TableRow key={index}>
                        <TableCell>{getStudentLabel(s)}</TableCell>
                        <TableCell>{s.className || '—'}</TableCell>
                        <TableCell>{`${Number(s.attendanceRate ?? 0).toFixed(1)}%`}</TableCell>
                      </TableRow>
                    ))}
                    {(!advancedAnalytics?.riskManagement?.chronicAbsentees || advancedAnalytics.riskManagement.chronicAbsentees.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          {t('schoolAdmin.dashboard.noChronicAbsentees') || 'No chronic absentees in the last 30 days'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Fee Debtors */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schoolAdmin.dashboard.riskTopDebtorsTitle') || 'Top Fee Debtors'}
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('schoolAdmin.dashboard.studentName') || 'Student'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.class') || 'Class'}</TableCell>
                      <TableCell>{t('schoolAdmin.dashboard.outstandingFees') || 'Outstanding Fees'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(advancedAnalytics?.riskManagement?.overdueFeesTopDebtors || []).map((s, index) => (
                      <TableRow key={index}>
                        <TableCell>{getStudentLabel(s)}</TableCell>
                        <TableCell>{s.className || '—'}</TableCell>
                        <TableCell>{formatCFA(s.balance || 0)}</TableCell>
                      </TableRow>
                    ))}
                    {(!advancedAnalytics?.riskManagement?.overdueFeesTopDebtors || advancedAnalytics.riskManagement.overdueFeesTopDebtors.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          {t('schoolAdmin.dashboard.noTopDebtors') || 'No outstanding fee debtors for this year'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Custom Analytics Dialog removed */}
    </Box>
  );
};

// Enhanced Summary Card Component (trend section only shown when real data is provided)
const SummaryCard = ({ title, value, icon, color, trend, trendDirection }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ color: `${color}.main`, mr: 1 }}>
          {icon}
        </Box>
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Typography variant="h4" color={`${color}.main`} sx={{ mb: 1 }}>
        {value?.toLocaleString() || '0'}
      </Typography>
      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            color={trendDirection === 'up' ? 'success.main' : trendDirection === 'down' ? 'error.main' : 'text.secondary'}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {trendDirection === 'up' && <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />}
            {trend}
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

export default SchoolOverviewPage;